import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Phone, User, Globe, CheckCircle, ExternalLink, Mail, Star } from "lucide-react";
import { trpc } from "@/lib/trpc";

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const COUNTRIES = [
  { id: "usa", flag: "🇺🇸", name: "АНУ", nameEn: "USA" },
  { id: "australia", flag: "🇦🇺", name: "Австрали", nameEn: "Australia" },
  { id: "canada", flag: "🇨🇦", name: "Канад", nameEn: "Canada" },
  { id: "schengen", flag: "🇪🇺", name: "Шенген", nameEn: "Schengen" },
  { id: "czech", flag: "🇨🇿", name: "Чех", nameEn: "Czech Republic" },
  { id: "ireland", flag: "🇮🇪", name: "Ирланд", nameEn: "Ireland" },
  { id: "korea", flag: "🇰🇷", name: "Солонгос", nameEn: "South Korea" },
  { id: "uk", flag: "🇬🇧", name: "Англи", nameEn: "UK" },
  { id: "new-zealand", flag: "🇳🇿", name: "Шинэ Зеланд", nameEn: "New Zealand" },
];

interface Service {
  id: string;
  name: string;
  description: string;
  bullets: string[];
  duration: string;
  price: number;
  qpayLink: string;
  highlight?: "vip" | "work" | "special";
}

const TRAVEL_LINK = "https://paylink.mn/api/s/inv_mrzx7kegbce69b";
const STUDY_LINK = "https://paylink.mn/api/s/inv_mrzx8tr30dd7d5";
const IMMIGRATION_LINK = "https://paylink.mn/api/s/inv_mrzxadzm4dbfd5";
const VIP_LINK = "https://paylink.mn/api/s/inv_mrzxbxln5bf605";

const VIP_SERVICE = (countryName: string, isUSA = false): Service => ({
  id: `${countryName.toLowerCase()}-vip`,
  name: `${countryName} VIP уулзалт — Дэлгэрэнгүй үнэлгээ`,
  description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
  bullets: [
    "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
    "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
    ...(isUSA ? ["Ногоон карт / байнгын оршин суух спонсор олох"] : []),
    "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
  ],
  duration: "2 цаг",
  price: 199000,
  qpayLink: VIP_LINK,
});

const IMMIGRATION_SERVICE = (countryName: string): Service => ({
  id: `${countryName.toLowerCase()}-immigration`,
  name: `${countryName} цагаачлалын зөвлөгөө`,
  description: "Цагаачлалын өмгөөлөгчтэй холбох болон бусад асуудал",
  bullets: [
    "Цагаачлалын өмгөөлөгчтэй холбох",
    "Ажлын спонсор олоход туслах",
    "Таны онцлогт тохирсон бусад боломжууд",
  ],
  duration: "45 минут",
  price: 159000,
  qpayLink: IMMIGRATION_LINK,
});

const SERVICES_BY_COUNTRY: Record<string, Service[]> = {
  usa: [
    {
      id: "usa-work-green",
      name: "АНУ ажлын виз, амьдрах эрх авах",
      description: "EB3, EB1-2, H2B, H1B, O1, L1 болон бусад ангиллын дэлгэрэнгүй зөвлөгөө",
      bullets: [
        "EB3 Unskilled — өндөр ур чадвар шаардахгүй, гэр бүлээрээ ажлын шугамаар ногоон карт авах",
        "EB NIW — ажил олгогчгүйгээр өөрөөрөө гэр бүлээрээ ногоон карт авах",
        "EB1-2 — онцгой авьяас, гавьяагаар ногоон карт авах",
        "H2B — улирлын ажлын виз (ресторан, зочид буудал, хөдөө аж ахуй)",
        "H1B — мэргэжлийн ажлын виз (IT, инженер, санхүү)",
        "O1 — онцгой авьяас, спорт, урлагийн виз",
        "L1 — компанийн дотоод шилжилт хөдөлгөөний виз",
        "Таны нөхцөлд хамгийн тохирох ангиллыг тодорхойлох",
      ],
      duration: "1 цаг",
      price: 99000,
      qpayLink: "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f",
      highlight: "work" as const,
    },
    {
      id: "usa-b1b2",
      name: "АНУ аялал B1/B2 зөвлөгөө",
      description: "Аялалын виз мэдүүлэх бүрэн зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Анх удаа эсвэл дахин мэдүүлэх стратеги",
        "Шаардлагатай материалын жагсаалт",
        "Ярилцлагад бэлдэх зөвлөмж",
        "Бондоо найдвартай буцааж авах арга",
        "DS-160 анкет шинээр үүсгэх зааварчилгаа",
        "Таны эрсдлийг тодорхойлох",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: "https://paylink.mn/api/s/inv_mrz8edg071e04d",
    },
    {
      id: "usa-f1f2",
      name: "АНУ сургалт F1/F2 зөвлөгөө",
      description: "Оюутны виз мэдүүлэх бүрэн зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Статусаа хадгалах (F-1 status maintenance)",
        "Хөтөлбөр болон анги шилжихэд анхаарах зүйлс",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Гэр бүлийн F-2 визний мэдээлэл",
        "Тэтгэлэгт зуучлах",
        "Ажил эрхлэх боломжууд (OPT, CPT)",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "usa-immigration",
      name: "АНУ цагаачлал",
      description: "Цагаачлалын өмгөөлөгчтэй холбох болон бусад асуудал",
      bullets: [
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Ажлын спонсор олоход туслах",
        "Орогнолын асуудал — хэрхэн хандах зааварчилгаа",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    {
      id: "usa-vip",
      name: "АНУ VIP уулзалт — Дэлгэрэнгүй үнэлгээ",
      description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
      bullets: [
        "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
        "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
        "Ногоон карт / байнгын оршин суух спонсор олох",
        "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
      ],
      duration: "2 цаг",
      price: 199000,
      qpayLink: VIP_LINK,
      highlight: "vip" as const,
    },
  ],
  australia: [
    {
      id: "au-travel",
      name: "Австрали аялалын виз (600)",
      description: "Аялалын виз мэдүүлэх зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Subclass 600 визний шаардлага",
        "Online profile нээх зааварчилгаа",
        "Шаардлагатай материалын тайлбар",
        "Захидлын заавар (cover letter)",
        "Татгалзлыг бууруулах арга",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "au-study",
      name: "Австрали оюутны виз (500)",
      description: "Оюутны виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Subclass 500 визний шаардлага",
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Татгалзлыг бууруулах арга",
        "Ажил эрхлэх боломжууд (ажиллах цагийн хязгаар)",
        "Тэтгэлэгт зуучлах",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "au-immigration",
      name: "Австрали цагаачлалын зөвлөгөө",
      description: "Австралид байнга оршин суух болон ажлын визний зөвлөгөө",
      bullets: [
        "Ажлын визний төрлүүд (Subclass 482, 186, 189, 190)",
        "Skilled Nominated болон Employer Sponsored ангиллын тайлбар",
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Ажлын спонсор олоход туслах",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    {
      id: "au-vip",
      name: "Австрали VIP уулзалт — Дэлгэрэнгүй үнэлгээ",
      description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
      bullets: [
        "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
        "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
        "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
      ],
      duration: "2 цаг",
      price: 199000,
      qpayLink: VIP_LINK,
      highlight: "vip" as const,
    },
  ],
  canada: [
    {
      id: "ca-travel",
      name: "Канад аялалын виз (TRV)",
      description: "Аялалын виз мэдүүлэх зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "TRV визний шаардлага",
        "GCKEY profile нээх зааварчилгаа",
        "Шаардлагатай материалын тайлбар",
        "Захидлын заавар (cover letter)",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ca-study",
      name: "Канад оюутны виз",
      description: "Оюутны виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курс (ESL/FSL) боломжууд",
        "Study Permit шаардлага ба процесс",
        "Тохиромжит сургуулийг сонгох зааварчилгаа",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "Тэтгэлэгт зуучлах",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "ca-immigration",
      name: "Канад цагаачлалын зөвлөгөө",
      description: "Канадад байнга оршин суух болон ажлын визний зөвлөгөө",
      bullets: [
        "Express Entry — магадлалаа нэмэгдүүлэх арга",
        "Provincial Nominee Program (PNP) тайлбар",
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Ажлын спонсор олоход туслах",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    {
      id: "ca-vip",
      name: "Канад VIP уулзалт — Дэлгэрэнгүй үнэлгээ",
      description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
      bullets: [
        "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
        "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
        "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
      ],
      duration: "2 цаг",
      price: 199000,
      qpayLink: VIP_LINK,
      highlight: "vip" as const,
    },
  ],
  schengen: [
    {
      id: "sch-travel",
      name: "Шенген аялалын виз",
      description: "Шенгений аялалын виз мэдүүлэх зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Шенгений виз мэдүүлэх ерөнхий шаардлага",
        "Шаардлагатай баримт бичгийн жагсаалт",
        "Татгалзлыг бууруулах арга",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
  ],
  czech: [
    {
      id: "cz-labor",
      name: "Чехийн хөдөлмөрийн гэрээ",
      description: "Гэрээт үйлдвэрт ажиллах бүрэн зөвлөгөө — элчингийн цаг, материал, тосох, байр",
      bullets: [
        "Гэрээт үйлдвэрүүдийн танилцуулга (хоол, цахилгаан, машин, тавилга үйлдвэр гэх мэт)",
        "Элчингийн цаг захиалах зааварчилгаа",
        "Шаардлагатай материал бүрдүүлэлт",
        "Нисэх онгоцоор тосох, байрны мэдээлэл",
        "Хөдөлмөрийн гэрээний нөхцөл, эрхийн тайлбар",
        "Ажлын визний процессын дэлгэрэнгүй тайлбар",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: TRAVEL_LINK,
      highlight: "special" as const,
    },
  ],
  ireland: [
    {
      id: "ie-travel",
      name: "Ирланд аялалын виз",
      description: "Аялалын виз мэдүүлэх зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Ирландын аялалын виз мэдүүлэх шаардлага",
        "Шаардлагатай баримт бичгийн жагсаалт",
        "Татгалзлыг бууруулах арга",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ie-study",
      name: "Ирланд оюутны виз",
      description: "Англи хэлний сургуулын виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Шаардлагатай материал бүрдүүлэлт",
        "Татгалзсан тохиолдолд давж заалдалт",
        "Төлбөрөө хурдан буцааж авах арга",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  korea: [
    {
      id: "kr-travel",
      name: "Солонгос аялалын виз",
      description: "Аялалын виз мэдүүлэх зөвлөгөө",
      bullets: [
        "Солонгосын аялалын виз мэдүүлэх шаардлага",
        "Шаардлагатай баримт бичгийн жагсаалт",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "kr-study",
      name: "Солонгос оюутны виз",
      description: "Оюутны виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Оюутны виз мэдүүлэх шаардлага",
        "Сургуулийн сонголт болон элсэлт",
        "Хэлний курсээр суралцах боломж",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "kr-immigration",
      name: "Солонгос цагаачлалын зөвлөгөө",
      description: "Цагаачлалын өмгөөлөгчтэй холбох болон бусад асуудал",
      bullets: [
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Ажлын спонсор олоход туслах",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    {
      id: "kr-vip",
      name: "Солонгос VIP уулзалт — Дэлгэрэнгүй үнэлгээ",
      description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
      bullets: [
        "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
        "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
        "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
      ],
      duration: "2 цаг",
      price: 199000,
      qpayLink: VIP_LINK,
      highlight: "vip" as const,
    },
  ],
  uk: [
    {
      id: "uk-travel",
      name: "Их Британи аялалын виз",
      description: "Аялалын виз мэдүүлэх зөвлөгөө — татгалзлыг бууруулах арга",
      bullets: [
        "Стандарт зочин визний шаардлага",
        "Шаардлагатай баримт бичгийн жагсаалт",
        "Татгалзлыг бууруулах арга",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "uk-study",
      name: "Их Британи оюутны виз",
      description: "Оюутны виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Student visa шаардлага",
        "Сургуулийн сонголт болон элсэлт",
        "Хэлний курсээр суралцах боломж",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "Тэтгэлэгт зуучлах",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  "new-zealand": [
    {
      id: "nz-skilled",
      name: "Шинэ Зеланд мэргэжлээрээ ажиллах зөвлөгөө",
      description: "Мэргэжлээрээ ашиглан визээр ажиллах бүрэн зөвлөгөө",
      bullets: [
        "Таны боломжийг тодорхойлох",
        "Эртнээс бэлдэж эхлэх зааварчилгаа",
        "Таньд тохирсон визийг тодорхойлох",
        "Skilled Migrant Category ба Essential Skills визний шаардлага",
        "Мэргэжлийг хүлээн зөвшөөрүүлэх арга",
        "Ажил олгогч олоход туслах боломжийн тойм",
      ],
      duration: "45 минут",
      price: 69000,
      qpayLink: "https://paylink.mn/api/s/inv_mrzynhjf4a66af",
      highlight: "special" as const,
    },
    {
      id: "nz-travel",
      name: "Шинэ Зеланд аялалын виз",
      description: "Аялалын виз мэдүүлэх зөвлөгөө",
      bullets: [
        "Visitor visa шаардлага",
        "Шаардлагатай баримт бичгийн жагсаалт",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "nz-study",
      name: "Шинэ Зеланд оюутны виз",
      description: "Оюутны виз мэдүүлэх зөвлөгөө — хэлний курсээр явах, ажил эрхлэх боломжууд",
      bullets: [
        "Student visa шаардлага",
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Санхүүгийн баримт бичгийн шаардлага",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
}
const STEP_LABELS = ["Мэдээлэл", "Улс", "Үйлчилгээ"];

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string }>({});

  const resetAndClose = () => {
    setStep(1);
    setName("");
    setPhone("");
    setEmail("");
    setSelectedCountry(null);
    setSelectedService(null);
    setErrors({});
    onClose();
  };

  const validateStep1 = () => {
    const newErrors: { name?: string; phone?: string; email?: string } = {};
    if (!name.trim()) newErrors.name = "Нэрээ оруулна уу";
    if (!phone.trim()) newErrors.phone = "Утасны дугаар оруулна уу";
    else if (!/^[0-9]{8}$/.test(phone.replace(/\s/g, ""))) newErrors.phone = "8 оронтой дугаар оруулна уу";
    if (!email.trim()) newErrors.email = "Мэйл хаягаа оруулна уу";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Зөв мэйл хаяг оруулна уу";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const notifyLead = trpc.callbacks.notifyConsultationLead.useMutation();
  const submitRequest = trpc.consultation.submitRequest.useMutation();

  const handleStep1Next = () => {
    if (validateStep1()) {
      // Fire-and-forget: notify owner via email
      notifyLead.mutate({ name, phone, email });
      setStep(2);
    }
  };

  const handleQPayClick = (service: Service) => {
    // Save consultation request with selected service info to DB
    if (name && phone && email && selectedCountry) {
      submitRequest.mutate({
        name,
        phone,
        email,
        country: selectedCountry,
        visaCategory: service.name,
        travelTimeline: "Төлөлөхгүй",
        callTime: "now",
        serviceName: service.name,
        servicePrice: service.price,
      });
    }
  };

  const handleCountrySelect = (countryId: string) => {
    setSelectedCountry(countryId);
    setSelectedService(null);
    setStep(3);
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
  };

  const services = selectedCountry ? SERVICES_BY_COUNTRY[selectedCountry] || [] : [];
  const country = COUNTRIES.find((c) => c.id === selectedCountry);

  // Progress bar percentage
  const progressPct = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={(e) => e.target === e.currentTarget && resetAndClose()}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl"
            style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #1a1040 100%)", border: "1px solid rgba(124,58,237,0.3)" }}
          >
            {/* Progress bar */}
            <div className="h-1 w-full" style={{ background: "rgba(124,58,237,0.15)" }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #7C3AED, #4F46E5, #3B82F6)" }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
            </div>

            {/* Header */}
            <div className="px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(124,58,237,0.2)" }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">Зөвлөгөө авах</h2>
                  <p className="text-sm mt-0.5" style={{ color: "rgba(167,139,250,0.8)" }}>
                    {step === 1 && "Таны мэдээлэл"}
                    {step === 2 && "Улс сонгоно уу"}
                    {step === 3 && (country ? `${country.flag} ${country.name} — Үйлчилгээ сонгоно уу` : "Үйлчилгээ сонгоно уу")}
                  </p>
                </div>
                <button
                  onClick={resetAndClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: "rgba(124,58,237,0.2)", color: "rgba(167,139,250,0.8)" }}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Step indicators */}
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0"
                        style={{
                          background: step >= s ? "linear-gradient(135deg, #7C3AED, #4F46E5)" : "rgba(124,58,237,0.15)",
                          color: step >= s ? "white" : "rgba(167,139,250,0.4)",
                          border: step === s ? "2px solid #A78BFA" : "2px solid transparent",
                        }}
                      >
                        {step > s ? <CheckCircle size={12} /> : s}
                      </div>
                      <span
                        className="text-xs hidden sm:block"
                        style={{ color: step >= s ? "rgba(167,139,250,0.9)" : "rgba(167,139,250,0.35)" }}
                      >
                        {STEP_LABELS[s - 1]}
                      </span>
                    </div>
                    {s < 3 && (
                      <div
                        className="h-px flex-1 rounded"
                        style={{ background: step > s ? "linear-gradient(90deg, #7C3AED, #4F46E5)" : "rgba(124,58,237,0.2)" }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              <AnimatePresence mode="wait">

                {/* Step 1: Name, Phone, Email */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                        <User size={13} className="inline mr-1.5" />
                        Таны нэр <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Жишээ: Болд Баатар"
                        className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                        style={{
                          background: "rgba(124,58,237,0.1)",
                          border: errors.name ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)",
                        }}
                        onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                        onBlur={(e) => (e.target.style.border = errors.name ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                      />
                      {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                        <Phone size={13} className="inline mr-1.5" />
                        Утасны дугаар <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
                        placeholder="8 оронтой дугаар"
                        maxLength={8}
                        className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                        style={{
                          background: "rgba(124,58,237,0.1)",
                          border: errors.phone ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)",
                        }}
                        onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                        onBlur={(e) => (e.target.style.border = errors.phone ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                      />
                      {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "rgba(167,139,250,0.9)" }}>
                        <Mail size={13} className="inline mr-1.5" />
                        Мэйл хаяг <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="example@gmail.com"
                        className="w-full px-4 py-3 rounded-xl text-white placeholder-gray-500 outline-none transition-all"
                        style={{
                          background: "rgba(124,58,237,0.1)",
                          border: errors.email ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)",
                        }}
                        onFocus={(e) => (e.target.style.border = "1px solid rgba(124,58,237,0.8)")}
                        onBlur={(e) => (e.target.style.border = errors.email ? "1px solid #EF4444" : "1px solid rgba(124,58,237,0.3)")}
                      />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                      <p className="text-xs mt-1" style={{ color: "rgba(167,139,250,0.5)" }}>
                        Зөвлөгөөний баталгаажуулалт мэйлээр явуулна
                      </p>
                    </div>

                    <div
                      className="rounded-xl p-3 text-xs"
                      style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.2)", color: "rgba(167,139,250,0.7)" }}
                    >
                      🔒 Таны мэдээлэл зөвхөн зөвлөгөө авах зорилгоор ашиглагдана.
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Country selection */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="grid grid-cols-2 gap-2.5">
                      {COUNTRIES.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => handleCountrySelect(c.id)}
                          className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all hover:scale-[1.02]"
                          style={{
                            background: "rgba(124,58,237,0.1)",
                            border: "1px solid rgba(124,58,237,0.25)",
                            color: "white",
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = "rgba(124,58,237,0.25)";
                            (e.currentTarget as HTMLButtonElement).style.border = "1px solid rgba(124,58,237,0.6)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.background = "rgba(124,58,237,0.1)";
                            (e.currentTarget as HTMLButtonElement).style.border = "1px solid rgba(124,58,237,0.25)";
                          }}
                        >
                          <span className="text-2xl">{c.flag}</span>
                          <span className="font-medium text-sm">{c.name}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Service selection */}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-3"
                  >
                    {services.map((service) => {
                      const isSelected = selectedService?.id === service.id;
                      const isVip = service.highlight === "vip";
                      const isWork = service.highlight === "work";
                      const isSpecial = service.highlight === "special";
                      const highlightBg = isVip
                        ? isSelected ? "rgba(234,179,8,0.2)" : "rgba(234,179,8,0.07)"
                        : isWork
                        ? isSelected ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.07)"
                        : isSpecial
                        ? isSelected ? "rgba(249,115,22,0.2)" : "rgba(249,115,22,0.07)"
                        : isSelected ? "rgba(124,58,237,0.25)" : "rgba(124,58,237,0.08)";
                      const highlightBorder = isVip
                        ? isSelected ? "1px solid rgba(234,179,8,0.7)" : "1px solid rgba(234,179,8,0.3)"
                        : isWork
                        ? isSelected ? "1px solid rgba(16,185,129,0.7)" : "1px solid rgba(16,185,129,0.3)"
                        : isSpecial
                        ? isSelected ? "1px solid rgba(249,115,22,0.7)" : "1px solid rgba(249,115,22,0.3)"
                        : isSelected ? "1px solid rgba(124,58,237,0.8)" : "1px solid rgba(124,58,237,0.2)";
                      const checkColor = isVip ? "#FBBF24" : isWork ? "#34D399" : isSpecial ? "#FB923C" : "#A78BFA";
                      const badgeIcon = isVip ? <Star size={13} className="text-yellow-400 flex-shrink-0" /> : isWork ? <span className="text-green-400 text-xs flex-shrink-0">💼</span> : isSpecial ? <span className="text-orange-400 text-xs flex-shrink-0">⭐</span> : null;
                      return (
                        <div key={service.id}>
                          <button
                            onClick={() => handleServiceSelect(service)}
                            className="w-full text-left rounded-xl p-4 transition-all"
                            style={{ background: highlightBg, border: highlightBorder }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {badgeIcon}
                                  <span className="font-semibold text-white text-sm">{service.name}</span>
                                  {isSelected && <CheckCircle size={13} className="text-violet-400 flex-shrink-0" />}
                                </div>
                                <p className="text-xs mt-1" style={{ color: "rgba(167,139,250,0.65)" }}>
                                  {service.description}
                                </p>
                                {/* Bullet points */}
                                <ul className="mt-2 space-y-1">
                                  {service.bullets.map((b, i) => (
                                    <li key={i} className="flex items-start gap-1.5 text-xs" style={{ color: "rgba(167,139,250,0.75)" }}>
                                      <span className="mt-0.5 flex-shrink-0" style={{ color: checkColor }}>✓</span>
                                      {b}
                                    </li>
                                  ))}
                                </ul>
                                <div className="flex items-center gap-2 mt-2">
                                  <span
                                    className="inline-block text-xs px-2 py-0.5 rounded-full"
                                    style={{ background: "rgba(124,58,237,0.2)", color: "rgba(167,139,250,0.9)" }}
                                  >
                                    ⏱ {service.duration}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-bold text-white text-base">
                                  ₮{service.price.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </button>

                          {/* QPay button + contract discount note */}
                          <AnimatePresence>
                            {isSelected && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-2 space-y-2">
                                  <a
                                    href={service.qpayLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleQPayClick(service)}
                                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:scale-[1.01]"
                                    style={{
                                      background: isVip
                                        ? "linear-gradient(135deg, #D97706 0%, #B45309 100%)"
                                        : isWork
                                        ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                                        : isSpecial
                                        ? "linear-gradient(135deg, #EA580C 0%, #C2410C 100%)"
                                        : "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                                      boxShadow: isVip
                                        ? "0 4px 20px rgba(217,119,6,0.35)"
                                        : isWork
                                        ? "0 4px 20px rgba(5,150,105,0.4)"
                                        : isSpecial
                                        ? "0 4px 20px rgba(234,88,12,0.4)"
                                        : "0 4px 20px rgba(124,58,237,0.4)",
                                    }}
                                  >
                                    <span>💳 QPay-ээр төлөх</span>
                                    <span className="font-normal text-sm opacity-80">— ₮{service.price.toLocaleString()}</span>
                                    <ExternalLink size={13} className="opacity-70" />
                                  </a>
                                  {/* Contract discount note */}
                                  <div
                                    className="rounded-lg px-3 py-2 text-xs text-center"
                                    style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", color: "rgba(134,239,172,0.85)" }}
                                  >
                                    💡 Манайхтай <strong>гэрээ байгуулан ажиллах</strong> нөхцөлд зөвлөгөөний төлбөр хасагдана
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 pt-3" style={{ borderTop: "1px solid rgba(124,58,237,0.15)" }}>
              <div className="flex gap-3">
                {step > 1 && (
                  <button
                    onClick={() => {
                      if (step === 3) { setSelectedService(null); setStep(2); }
                      else setStep(step - 1);
                    }}
                    className="flex-1 py-3 rounded-xl font-medium transition-all"
                    style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.3)", color: "rgba(167,139,250,0.9)" }}
                  >
                    ← Буцах
                  </button>
                )}

                {step === 1 && (
                  <button
                    onClick={handleStep1Next}
                    className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)" }}
                  >
                    Үргэлжлүүлэх <ChevronRight size={16} />
                  </button>
                )}

                {step === 2 && (
                  <div className="flex-1 text-center text-sm py-3" style={{ color: "rgba(167,139,250,0.6)" }}>
                    <Globe size={14} className="inline mr-1.5" />
                    Улсаа сонгоно уу
                  </div>
                )}

                {step === 3 && !selectedService && (
                  <div className="flex-1 text-center text-sm py-3" style={{ color: "rgba(167,139,250,0.6)" }}>
                    Үйлчилгээ сонгоно уу
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
